import { sizeMap } from '@/constants/size-map';
import { WebsiteEnv } from '@/constants/website-env';
import { findMatchingVariant } from '@/util/findMatchingProductVariant';
import { isProductConfigurable } from '@/util/isProductConfigurable';

export const INITIAL_OPTION_CODES = new Map();
export const INITIAL_OPTION_SELECTIONS = new Map();
export const OUT_OF_STOCK_CODE = 'OUT_OF_STOCK';
export const IN_STOCK_CODE = 'IN_STOCK';

export const normlizePath = url => {
  return url.length > 1 && url[0] === '/' ? url.substring(1) : url;
};

export const deriveOptionCodesFromProduct = product => {
  // If this is a simple product it has no option codes.
  if (!isProductConfigurable(product)) {
    return INITIAL_OPTION_CODES;
  }

  // Initialize optionCodes based on the options of the product.
  const initialOptionCodes = new Map();

  for (const { attribute_id, attribute_code } of product.configurable_options) {
    initialOptionCodes.set(attribute_id, attribute_code);
  }

  return initialOptionCodes;
};

const getIsSelected = ({ product, optionSelections }) => {
  const optionsSelectedLength = Array.from(optionSelections.values()).filter(value => !!value).length;

  if (product?.__typename === 'SimpleProduct') {
    return true;
  }

  if (product?.__typename === 'ConfigurableProduct') {
    // 没有选完
    if (product?.configurable_options?.length > optionsSelectedLength) {
      return false;
    }

    return true;
  }
};

export const getIsOutOfStock = (product, optionCodes, optionSelections) => {
  const { stock_status, variants } = product;
  const isConfigurable = isProductConfigurable(product);
  // const optionsSelected =
  //       Array.from(optionSelections.values()).filter((value) => !!value)
  //         .length > 0

  const optionsSelected = getIsSelected({
    product,
    optionSelections,
  });

  if (isConfigurable && optionsSelected) {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    const stockStatus = item?.product?.stock_status;

    return stockStatus === OUT_OF_STOCK_CODE || !stockStatus;
  }

  return stock_status === OUT_OF_STOCK_CODE;
};

export const getSelctedProduct = (product, optionCodes, optionSelections) => {
  const { variants } = product;
  const isConfigurable = isProductConfigurable(product);
  const optionsSelected = Array.from(optionSelections.values()).filter(value => !!value).length > 0;

  if (isConfigurable && optionsSelected) {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    return item?.product || null;
  }

  return null;
};

export const getIsAllOutOfStock = product => {
  const { stock_status, variants } = product;
  const isConfigurable = isProductConfigurable(product);

  if (isConfigurable) {
    const inStockItem = variants.find(item => {
      return item.product.stock_status === IN_STOCK_CODE;
    });

    return !inStockItem;
  }

  return stock_status === OUT_OF_STOCK_CODE;
};

export const checkProductHasFabric = product => {
  const { ampProtOption } = product || {};
  const { items } = ampProtOption || {};

  if (!items) {
    return false;
  }

  const hasFabric = items.some(item => item.label.toLowerCase() === 'fabric');

  return hasFabric;
};

export const checkOptionsSelected = (optionSelections, isProductHasFabric) => {
  const nonEmptySelections = Array.from(optionSelections.values()).filter(value => !!value).length;

  if (isProductHasFabric) {
    return nonEmptySelections > 1;
  }

  return nonEmptySelections > 0;
};

export const getMediaGalleryEntries = (product, optionCodes, optionSelections, isMobile) => {
  let value = [];

  if (!product) {
    return '';
  }

  const { media_gallery_entries, variants, media_gallery } = product;
  const isConfigurable = isProductConfigurable(product);

  const isProductHasFabric = checkProductHasFabric(product);

  const optionsSelected = checkOptionsSelected(optionSelections, isProductHasFabric);

  if (!isConfigurable || !optionsSelected) {
    // 简单产品根据选择的颜色切换图片
    if (!optionsSelected) {
      const copy = media_gallery?.concat([]);

      value = copy?.map(temp => {
        return Object.assign({}, temp, {
          file: temp.image,
        });
      });
    } else {
      value = changeProductImage(product, optionSelections, media_gallery_entries) || media_gallery_entries;
    }
  } else {
    // If any of the possible variants matches the selection add that
    // variant's image to the media gallery. NOTE: This _can_, and does,
    // include variants such as size. If Magento is configured to display
    // an image for a size attribute, it will render that image.

    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    const copy = media_gallery?.concat([]);

    value = item
      ? [
          ...(item?.product?.media_gallery?.concat([]).map(temp => {
            return Object.assign({}, temp, {
              file: temp.image,
            });
          }) || []),
        ]
      : copy.map(temp => {
          return Object.assign({}, temp, {
            file: temp.image,
          });
        });
  }

  return value;
};

// 找到交集
function findIntersection(array1, array2) {
  // 将第一个数组转换为 Map
  const map = new Map();

  for (const item of array1) {
    map.set(item.attribute_id, item);
  }

  // 遍历第二个数组，查找交集
  const intersection = [];

  for (const item of array2) {
    if (map.has(item.key)) {
      intersection.push(map.get(item.key));
    }
  }

  return intersection;
}

export const getColorLabel = (product, optionSelections) => {
  const options = product?.ampProtOption?.items;
  // 获取颜色optionId

  const colorLabels = options.filter(option => option.label.toLowerCase().includes('color'));
  const hasMultipleColors = colorLabels.length > 1;

  let colorOption;

  if (hasMultipleColors) {
    const result = [];

    for (const [key, value] of optionSelections.entries()) {
      if (value) {
        result.push({
          key,
          value,
        });
      }
    }

    colorOption = findIntersection(colorLabels, result)?.[0];
  } else {
    colorOption = product?.ampProtOption?.items?.find(item => item.label.toLowerCase().includes('color'));
  }

  const optionId = colorOption?.attribute_id;
  // 把已选中的属性map对象转换成object并通过属性名(optionId)获取对应的属性值
  const optionValueId = Object.fromEntries(optionSelections)[optionId];

  if (optionValueId) {
    // 然后根据optionId获取optionValueId
    const colorLabel = colorOption?.values
      .find(item => item.value_index === optionValueId)
      ?.label?.replace(/\//g, '-')
      ?.replace(/\s+/g, '-');

    return colorLabel;
  }

  return false;
};

export const getFabricLabel = (product, optionSelections) => {
  const fabricOption = product?.ampProtOption?.items?.find(item => item.label.toLowerCase() == 'fabric');

  const optionId = fabricOption?.attribute_id;
  const optionValueId = Object.fromEntries(optionSelections)[optionId];

  if (optionValueId) {
    // 然后根据optionId获取optionValueId
    const fabricLabel = fabricOption?.values
      .find(item => item.value_index === optionValueId)
      ?.label?.replace(/\//g, '-');

    return fabricLabel;
  }

  return false;
};

export const getSku = product => {
  const sku = product.sku;

  return sku;
};

const sortColorSizeImage = (a, b, selectedUsSizeLabel) => {
  if (selectedUsSizeLabel === undefined || selectedUsSizeLabel === null || selectedUsSizeLabel === 'customized_size') {
    return 0;
  }

  const smallA = a.includes('-SmallSize');
  const smallB = b.includes('-SmallSize');
  const isSelectedSmallSize = selectedUsSizeLabel <= 14;
  let order = 0;

  if (isSelectedSmallSize) {
    order = (smallA ? 0 : 1) - (smallB ? 0 : 1);
  } else {
    order = (smallA ? 1 : 0) - (smallB ? 1 : 0);
  }

  if (order !== 0) return order;

  const defaultOrderA = parseInt(a.match(/\((\d+)\)-(BigSize|SmallSize)/)?.[1] || '0');
  const defaultOrderB = parseInt(b.match(/\((\d+)\)-(BigSize|SmallSize)/)?.[1] || '0');

  return defaultOrderA - defaultOrderB;
};

// optionSelections:  attribute_id -> value_index 的映射
export const changeProductImage = (product, optionSelections, media_gallery_entries) => {
  const isProductHasFabric = checkProductHasFabric(product);

  const optionsSelected = checkOptionsSelected(optionSelections, isProductHasFabric);

  if (optionsSelected) {
    // 存在 fabric 时候, 选中 fabric 和 color, optionsSelected 为 true
    // 不存在 fabric 时候, 选中 color, optionsSelected 为 true
    let newmedia_gallery_entries = [];

    const colorLabel = getColorLabel(product, optionSelections);
    const fabricLabel = getFabricLabel(product, optionSelections);
    const sku = getSku(product);
    const baseUrl =
      process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL_IMAGE +
      (process.env.NEXT_PUBLIC_BASE_PATH ? `${process.env.NEXT_PUBLIC_BASE_PATH.slice(1)}/` : '');
    const productImagePath = 'media/catalog/product/product_image/';
    const selectedColorOption = product.ampProtOption?.items?.find(option => {
      return !!(option?.default_label?.toLowerCase()?.includes('color') && optionSelections?.has(option.attribute_id));
    });
    const { sizeImages, normalImages, colorComparisonImages } = selectedColorOption?.values
      ?.find(x => x.value_index === optionSelections.get(selectedColorOption.attribute_id))
      ?.color_image?.find(x => (fabricLabel ? x?.fabric === fabricLabel : true))
      ?.url?.reduce(
        (acc, cur) => {
          if (!cur) return acc;

          if (cur.includes('-SmallSize') || cur.includes('-BigSize')) {
            acc.sizeImages.push(cur);
            return acc;
          }

          if (cur.includes('-Color-Comparison')) {
            acc.colorComparisonImages.push(cur);
            return acc;
          }

          acc.normalImages.push(cur);
          return acc;
        },
        { sizeImages: [], normalImages: [], colorComparisonImages: [] },
      ) || { sizeImages: [], normalImages: [], colorComparisonImages: [] };

    const defaultColorImages = normalImages.concat(colorComparisonImages);
    const selectedSizeOption = product.ampProtOption?.items?.find(option => {
      return !!(option?.default_label?.toLowerCase()?.includes('size') && optionSelections?.has(option.attribute_id));
    });
    let selectedUsSizeLabel = selectedSizeOption?.values?.find(
      x => x.value_index === optionSelections.get(selectedSizeOption.attribute_id),
    )?.default_label;

    if (selectedUsSizeLabel && !WebsiteEnv.isUnitedStates && !WebsiteEnv.isCanada) {
      selectedUsSizeLabel = Object.keys(sizeMap).find(x => sizeMap[x] === selectedUsSizeLabel);
    }

    selectedUsSizeLabel =
      typeof selectedUsSizeLabel === 'number' ? selectedUsSizeLabel : parseFloat(selectedUsSizeLabel);

    if (isProductHasFabric && colorLabel && fabricLabel) {
      const isDefaultFabric = fabricLabel.includes('-default');

      if (isDefaultFabric && sizeImages.length !== 0) {
        newmedia_gallery_entries = sizeImages
          .sort((a, b) => {
            return sortColorSizeImage(a, b, selectedUsSizeLabel);
          })
          .concat(colorComparisonImages)
          .map(x => {
            const imageFileName = `${sku}/${fabricLabel}/${x}`;

            return {
              file: `${baseUrl}${productImagePath}${imageFileName}`,
              label: '',
              uid: '',
            };
          });
      } else {
        // 存在 fabric 时候, 两个 label 都有, 改变图片
        newmedia_gallery_entries = defaultColorImages.sort().map(x => {
          const imageFileName = `${sku}/${fabricLabel}/${x}`;

          return {
            file: `${baseUrl}${productImagePath}${imageFileName}`,
            label: '',
            uid: '',
          };
        });
      }
    } else if (!isProductHasFabric && colorLabel) {
      if (sizeImages.length !== 0) {
        newmedia_gallery_entries = sizeImages
          .sort((a, b) => {
            return sortColorSizeImage(a, b, selectedUsSizeLabel);
          })
          .concat(colorComparisonImages)
          .map(x => {
            const imageFileName = `${sku}/${x}`;

            return {
              file: `${baseUrl}${productImagePath}${imageFileName}`,
              label: '',
              uid: '',
            };
          });
      } else {
        // 不存在 fabric 时候, 存在 colorLabel, 改变图片
        newmedia_gallery_entries = defaultColorImages.sort().map(x => {
          const imageFileName = `${sku}/${x}`;

          return {
            file: `${baseUrl}${productImagePath}${imageFileName}`,
            label: '',
            uid: '',
          };
        });
      }
    } else {
      return false;
    }

    return newmedia_gallery_entries;
  }

  return false;
};

// We only want to display breadcrumbs for one category on a PDP even if a
// product has multiple related categories. This function filters and selects
// one category id for that purpose.
export const getBreadcrumbCategoryId = categories => {
  // Exit if there are no categories for this product.
  if (!categories || !categories.length) {
    return;
  }

  const breadcrumbSet = new Set();

  categories.forEach(({ breadcrumbs }) => {
    // breadcrumbs can be `null`...
    (breadcrumbs || []).forEach(({ category_id }) => breadcrumbSet.add(category_id));
  });

  // Until we can get the single canonical breadcrumb path to a product we
  // will just return the first category id of the potential leaf categories.
  const leafCategory = categories.find(category => !breadcrumbSet.has(category.uid));

  // If we couldn't find a leaf category then just use the first category
  // in the list for this product.
  return leafCategory.uid || categories[0].uid;
};

export const getConfigPrice = (product, optionCodes, optionSelections) => {
  let value;

  const { variants } = product;
  const isConfigurable = isProductConfigurable(product);

  const optionsSelected = Array.from(optionSelections.values()).filter(value => !!value).length > 0;

  let changevalue;

  if (!isConfigurable || !optionsSelected) {
    value = product.price_range?.maximum_price;
    const changepriceoptions = product.ampProtOption?.items?.find(
      item => item.type == 'drop_down' && item.label != 'Color',
    )?.values;

    if (changepriceoptions) {
      const changepriceoption = optionsSelected
        ? changepriceoptions?.find(item =>
            Array.from(optionSelections)
              .map(os => os[1])
              .includes(item.value_index),
          )
        : null;

      if (changepriceoption) {
        changevalue = JSON.parse(JSON.stringify(value));
        changevalue.final_price.value = Number(
          (Number(value.final_price.value) + Number(changepriceoption.price)).toFixed(2),
        );
      }
    }
  } else {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    value = item ? item.product.price_range?.maximum_price : product.price_range?.maximum_price;
  }

  return changevalue || value;
};

export const attributeLabelCompare = (attribute1, attribute2) => {
  const label1 = attribute1['attribute_metadata']['label'].toLowerCase();
  const label2 = attribute2['attribute_metadata']['label'].toLowerCase();

  if (label1 < label2) {
    return -1;
  } else if (label1 > label2) {
    return 1;
  }

  return 0;
};

export const getCustomAttributes = (product, optionCodes, optionSelections) => {
  const { custom_attributes, variants } = product;
  const isConfigurable = isProductConfigurable(product);
  const optionsSelected = Array.from(optionSelections.values()).filter(value => !!value).length > 0;

  if (isConfigurable && optionsSelected) {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    return [];
    // return item && item?.product
    //   ? [...item?.product?.custom_attributes].sort(attributeLabelCompare)
    //   : []
  }

  return custom_attributes ? [...custom_attributes].sort(attributeLabelCompare) : [];
};
